import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FaRegCopy, FaCheck, FaTimes } from 'react-icons/fa';
import { MailerooDnsRecordsResult } from '../../model/MailerooDnsRecordsResult';

interface IProps {
    result: MailerooDnsRecordsResult;
    onClose: () => void;
}

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    z-index: 2000;
    overflow: auto;
    padding: 3rem 1rem;
`;

const Dialog = styled.div`
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.25);
    width: min(1180px, 100%);
    padding: 1.25rem 1.5rem 1.5rem;

    .dns-modal__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5rem;
    }
    .dns-modal__head h3 { margin: 0; }
    .dns-modal__close {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 1.1rem;
        color: #666;
    }
    .dns-modal__warning {
        background: #fff8e1;
        border: 1px solid #ffe58f;
        border-radius: 4px;
        padding: 0.6rem 0.85rem;
        margin: 0.5rem 0;
        font-size: 0.85rem;
        color: #5c4b00;
    }
    .dns-modal__note {
        font-size: 0.8rem;
        color: #666;
        margin: 0.75rem 0 0;
    }
    .dns-modal__error {
        background: #fdecea;
        border: 1px solid #f5c6cb;
        border-radius: 4px;
        padding: 0.75rem 0.9rem;
        margin: 0.5rem 0;
        color: #a12;
        font-size: 0.9rem;
    }
    table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 0.75rem;
        table-layout: fixed;
    }
    th, td {
        border: 1px solid #e2e2e2;
        padding: 6px 8px;
        font-size: 0.78rem;
        text-align: left;
        vertical-align: top;
    }
    /* only the copyable hostname/value cells may break mid-word (long DKIM keys etc);
       descriptions wrap on word boundaries */
    .dns-modal__copyable code {
        word-break: break-all;
    }
    th { background: rgb(0, 181, 209); color: #fff; }
    .dns-modal__copyable {
        display: flex;
        align-items: flex-start;
        gap: 6px;
    }
    .dns-modal__copyable code {
        flex: 1;
        font-family: monospace;
    }
    .dns-modal__copyable.nowrap code {
        white-space: nowrap;
    }
    .dns-modal__copy {
        background: none;
        border: none;
        cursor: pointer;
        color: #0089a3;
        padding: 0;
        flex: 0 0 auto;
    }
    .dns-modal__copy.copied { color: #2e9b4f; }

    /* Mobile: collapse the 4-column table into one stacked, labelled card per record. Desktop is unchanged. */
    @media (max-width: 640px) {
        padding: 1rem 1rem 1.25rem;

        table { table-layout: auto; margin-top: 0.5rem; }
        thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
        tr {
            display: block;
            border: 1px solid #cfcfcf;
            border-radius: 4px;
            margin-bottom: 0.6rem;
        }
        td {
            display: block;
            border: none;
            border-bottom: 1px solid #f0f0f0;
            padding: 8px 10px;
        }
        td:last-child { border-bottom: none; }
        td::before {
            content: attr(data-label);
            display: block;
            font-weight: 700;
            font-size: 0.65rem;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            color: #888;
            margin-bottom: 3px;
        }
        /* long hostnames/values must wrap inside the narrow card */
        .dns-modal__copyable.nowrap code { white-space: normal; }
    }
`;

const Copyable: React.FC<{ text: string; nowrap?: boolean }> = ({ text, nowrap }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };
    return (
        <div className={`dns-modal__copyable${nowrap ? ' nowrap' : ''}`}>
            <code>{text}</code>
            <button
                type="button"
                className={`dns-modal__copy${copied ? ' copied' : ''}`}
                onClick={copy}
                title="Copy"
                aria-label="Copy"
            >
                {copied ? <FaCheck size={13} /> : <FaRegCopy size={13} />}
            </button>
        </div>
    );
};

const MailerooDnsRecordsModal: React.FC<IProps> = ({ result, onClose }) => {
    // A lookup failure returns a message and no records — show the error rather than any default records.
    const hasError = !result.records || result.records.length === 0;
    return createPortal(
        <Overlay onMouseDown={onClose}>
            <Dialog onMouseDown={e => e.stopPropagation()}>
                <div className="dns-modal__head">
                    <h3>Required DNS Records — {result.domain}</h3>
                    <button className="dns-modal__close" onClick={onClose} aria-label="Close"><FaTimes /></button>
                </div>
                { hasError ? (
                    <div className="dns-modal__error">
                        { result.message || 'Could not look up the existing SPF record. Please try again.' }
                    </div>
                ) : <>
                <div className="dns-modal__warning">
                    ⚠️ Edit your existing SPF record — don't create a new one. Add the bolded part.
                    Multiple SPF records will cause delivery failures.
                </div>
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '22%' }}>Description</th>
                            <th style={{ width: '7%' }}>Type</th>
                            <th style={{ width: '31%' }}>Hostname</th>
                            <th style={{ width: '40%' }}>Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {result.records.map((r, i) => (
                            <tr key={i}>
                                <td data-label="Description">{r.description}</td>
                                <td data-label="Type">{r.type}</td>
                                <td data-label="Hostname"><Copyable text={r.hostname} nowrap /></td>
                                <td data-label="Value"><Copyable text={r.value} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className="dns-modal__note">
                    After changes, test both directions: send from corporate → personal email, then
                    reply back. Verify both work.
                </p>
                </> }
            </Dialog>
        </Overlay>,
        document.body
    );
};

export default MailerooDnsRecordsModal;
